import { HttpError, IResourceComponentsProps, useList } from "@refinedev/core";
import { Edit, useForm, useSelect } from "@refinedev/antd";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  message,
  DatePicker,
  Switch,
} from "antd";
import Spin from "antd/lib/spin";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { apiUrlV2 } from "@/consts";
import { TOKEN_KEY } from "../../authProvider";
import { MediaUploadSection } from "@/components/MediaUploadSection";
import dayjs from "dayjs";

export const MissionEdit: React.FC<IResourceComponentsProps> = () => {
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadingFileName, setUploadingFileName] = useState<string | null>(
    null
  );
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setUploadingFileName(file.name);
    setUploadError(null);
    // Show pending in attachments
    setAttachments((prev: any[]) => [
      ...prev,
      { id: "pending", type: file.type, name: file.name, pending: true },
    ]);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const body = {
        fileCount: 1,
        mimeType: file.type.startsWith("image") ? "IMAGE" : "VIDEO",
        uploadType: "MISSION",
      };
      const res = await fetch(`${apiUrlV2}/file/presigned_url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const result = await res.json();
      const fileData = result?.data?.[0];
      if (!fileData) throw new Error("No file data returned");
      // S3 POST upload
      const formData = new FormData();
      Object.entries(fileData.fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append("Content-Type", file.type);
      formData.append("file", file);
      await fetch(fileData.url, {
        method: "POST",
        body: formData,
      });
      setAttachments((prev: any[]) => [
        ...prev.filter((m) => !(m.pending && m.name === file.name)),
        { id: fileData.fileID, type: file.type, name: file.name },
      ]);
    } catch (err) {
      setUploadError("Upload failed");
      setAttachments((prev: any[]) =>
        prev.filter((m) => !(m.pending && m.name === file.name))
      );
    }
    setUploading(false);
    setUploadingFileName(null);
  };
  const { formProps, saveButtonProps, query, onFinish, formLoading } = useForm<
    any,
    HttpError,
    any
  >({
    resource: "mission",
    successNotification: (data, values) => {
      return {
        message: data?.data.message,
        type: "success",
      };
    },
    errorNotification: (data, values) => {
      return {
        message: data?.response.data,
        type: "error",
      };
    },
    action: "edit",
  });

  const {
    selectProps: missionTypeProps,
    queryResult: { isLoading: missionTypeLoading },
  } = useSelect({
    resource: "mission_type/for_select_list",
    optionLabel: "value",
    optionValue: "slug",
  });

  const {
    selectProps: targetUnitProps,
    queryResult: { isLoading: targetUnitLoading },
  } = useSelect({
    resource: "target_unit/for_select_list",
    optionLabel: "value",
    optionValue: "id",
  });

  const {
    selectProps: workoutSelectProps,
    queryResult: { isLoading: workoutLoading },
  } = useSelect({
    resource: "workout/for_select_list",
    optionLabel: "value",
  });

  // Compute initial media from backend attachments
  const initialMedia = (query?.data?.data?.data?.attachments || [])
    .filter((a: any) => a.fileUpload && a.fileUpload.id)
    .map((a: any) => ({
      id: a.fileUpload.id,
      type: a.fileUpload.fileType || "image",
      name: a.fileUpload.fileName || "",
    }));

  const [attachments, setAttachments] = useState<any[]>(initialMedia);
  useEffect(() => {
    setAttachments(initialMedia);
  }, [query?.data?.data?.data?.attachments]);

  formProps.onFinish = (values) => {
    const submitValues = { ...values };

    if (submitValues.durationLimitMinutes) {
      const { amount, unit } = submitValues.durationLimitMinutes;
      let minutes = amount;
      switch (unit) {
        case "minutes":
          break;
        case "hours":
          minutes *= 60;
          break;
        case "days":
          minutes *= 1440;
          break;
        case "weeks":
          minutes *= 10080;
          break;
      }
      submitValues.durationLimitMinutes = minutes;
    } else {
      delete submitValues.durationLimitMinutes;
    }
    delete submitValues.hasDuration;
    if (submitValues.targetValue && submitValues.targetUnitID) {
      submitValues.missionTarget = {
        targetValue: submitValues.targetValue,
        targetUnitID: submitValues.targetUnitID,
      };
      delete submitValues.targetValue;
      delete submitValues.targetUnitID;
    }
    if (submitValues.workoutsSets && submitValues.workoutsSets.length > 0) {
      submitValues.missionRound = {
        rounds: submitValues.rounds,
        workouts: submitValues.workoutsSets[0].workouts || [],
      };
      delete submitValues.workoutsSets;
      delete submitValues.rounds;
    }
    if (attachments.length > 0) {
      submitValues.fileIDs = attachments.filter((m) => !m.pending).map((m) => m.id);
    }

    if (submitValues.endsAt) {
      submitValues.endsAt = submitValues.endsAt.toISOString
        ? submitValues.endsAt.toISOString()
        : submitValues.endsAt;
    }
    return onFinish(submitValues);
  };

  const missionData = query?.data?.data?.data;
  const missionType = missionData?.missionType;

  useEffect(() => {
    if (missionData && !targetUnitLoading && targetUnitProps?.options) {
      const targetUnitId = missionData?.missionTarget?.targetUnit?.id;
      if (targetUnitId) {
        formProps.form?.setFieldsValue({
          targetUnitID: targetUnitId,
        });
      }
    }
  }, [
    missionData,
    targetUnitLoading,
    targetUnitProps?.options,
    formProps.form,
  ]);

  const mergedSaveButtonProps = { ...saveButtonProps, disabled: uploading };

  return (
    <Edit
      saveButtonProps={mergedSaveButtonProps}
      isLoading={formLoading}
      resource="mission"
      mutationMode="optimistic"
      title="Edit Mission"
    >
      <Form {...formProps} layout="vertical">
        <Form.Item label="Featured" name={["isFeatured"]} valuePropName="checked" initialValue={missionData?.isFeatured}>
          <Switch />
        </Form.Item>
        <Form.Item label="Media">
          {attachments.length > 0 ? (
            <MediaUploadSection
              initialMedia={attachments}
              onChange={setAttachments}
            />
          ) : (
            <div>
              <Button
                type="primary"
                disabled={uploading}
                onClick={() =>
                  document.getElementById("media-upload-input")?.click()
                }
              >
                Upload Media
              </Button>
              <input
                id="media-upload-input"
                type="file"
                accept="image/*,video/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUpload(file);
                  e.target.value = "";
                }}
              />
              {uploadError && (
                <div style={{ color: "red", marginTop: 8 }}>{uploadError}</div>
              )}
            </div>
          )}
        </Form.Item>

        <Form.Item
          label="Name"
          name={["name"]}
          initialValue={missionData?.name}
          rules={[
            {
              required: true,
              message: "Please enter a name",
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Description"
          name={["description"]}
          initialValue={missionData?.description}
        >
          <Input />
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues?.typeID !== currentValues?.typeID
          }
        >
          {({ getFieldValue }) => {
            // const missionType = missionTypeProps?.options?.find(
            //     (option: any) => option.value === getFieldValue('typeID')
            // );
            const label = missionType?.name;

            if (label === "Health Stats") {
              return (
                <>
                  <Form.Item label="Target" required>
                    <Input.Group compact>
                      <Form.Item
                        name={["targetValue"]}
                        noStyle
                        initialValue={missionData?.missionTarget?.targetValue}
                        rules={[
                          {
                            required: true,
                            type: "number",
                            min: 0,
                            message: "Please enter a positive number",
                          },
                        ]}
                      >
                        <InputNumber
                          min={0}
                          step="1"
                          style={{ width: "50%" }}
                          placeholder="Enter target value"
                        />
                      </Form.Item>
                      <Form.Item
                        name={["targetUnitID"]}
                        noStyle
                        rules={[
                          {
                            required: true,
                          },
                        ]}
                      >
                        <Select
                          {...targetUnitProps}
                          loading={targetUnitLoading}
                          style={{ width: "50%" }}
                          placeholder={
                            targetUnitLoading
                              ? "Loading..."
                              : "Select target unit"
                          }
                        />
                      </Form.Item>
                    </Input.Group>
                  </Form.Item>
                  <div style={{ marginTop: 24 }} />
                  <Form.Item
                    label="Mode"
                    name={["mode"]}
                    initialValue={missionData?.missionTarget.missionMode}
                    rules={[
                      { required: true, message: "Please select a mode" },
                    ]}
                  >
                    <Select
                      options={[
                        { label: "Daily Stat", value: "DAILY_STAT" },
                        {
                          label: "Activity Session",
                          value: "ACTIVITY_SESSION",
                        },
                      ]}
                      placeholder="Select mode"
                      value={missionData?.mode || undefined}
                    />
                  </Form.Item>
                </>
              );
            } else if (
              label === "Rounds" ||
              label === "Calories" ||
              label === "Workouts" ||
              label === "First to Finish"
            ) {
              return (
                <>
                  <Form.Item
                    label="Rounds"
                    name="rounds"
                    initialValue={missionData?.missionRound?.rounds}
                    rules={[{ required: true, message: "Please enter rounds" }]}
                  >
                    <InputNumber min={1} />
                  </Form.Item>

                  <Form.List
                    name={["workoutsSets", 0, "workouts"]}
                    initialValue={missionData?.missionRound?.workouts?.map(
                      (w: any) => ({
                        workoutID: w.workoutID,
                        reps: w.reps,
                      })
                    )}
                    rules={[
                      {
                        validator: async (_, workouts) => {
                          if (!workouts || workouts.length < 1) {
                            return Promise.reject(
                              new Error("At least one workout is required")
                            );
                          }
                        },
                      },
                    ]}
                  >
                    {(
                      workoutFields,
                      { add: addWorkout, remove: removeWorkout }
                    ) => (
                      <>
                        {workoutFields.map((field) => (
                          <Space
                            key={field.key}
                            style={{ display: "flex", marginBottom: 8 }}
                            align="baseline"
                          >
                            <Form.Item
                              name={[field.name, "workoutID"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please select a workout",
                                },
                              ]}
                            >
                              <Select
                                {...workoutSelectProps}
                                loading={workoutLoading}
                                style={{ width: "200px" }}
                                placeholder="Select workout"
                              />
                            </Form.Item>
                            <Form.Item
                              name={[field.name, "reps"]}
                              rules={[
                                { required: true, message: "Missing reps" },
                              ]}
                            >
                              <InputNumber
                                placeholder="Reps"
                                min={1}
                                style={{ width: "100px" }}
                              />
                            </Form.Item>
                            <MinusCircleOutlined
                              onClick={() => {
                                if (workoutFields.length > 1) {
                                  removeWorkout(field.name);
                                } else {
                                  // Show error message or handle last workout removal
                                  message.warning(
                                    "At least one workout is required"
                                  );
                                }
                              }}
                            />
                          </Space>
                        ))}
                        <Form.Item>
                          <Button
                            type="dashed"
                            onClick={() => addWorkout()}
                            block
                            icon={<PlusOutlined />}
                          >
                            Add Workout
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </>
              );
            }
            return null;
          }}
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues?.missionTypeSlug !== currentValues?.missionTypeSlug
          }
        >
          {({ getFieldValue }) => {
            const missionType = missionTypeProps?.options?.find(
              (option: any) => option.value === getFieldValue("missionTypeSlug")
            );
            const label = missionType?.label;

            if (label === "Health Stats") {
              return (
                <Form.Item label="Target" required>
                  <Input.Group compact>
                    <Form.Item
                      name={["targetValue"]}
                      noStyle
                      rules={[
                        {
                          required: true,
                          type: "number",
                          min: 0,
                          message: "Please enter a positive number",
                        },
                      ]}
                    >
                      <InputNumber
                        min={0}
                        step="1"
                        style={{ width: "50%" }}
                        placeholder="Enter target value"
                      />
                    </Form.Item>
                    <Form.Item
                      name={["targetUnitSlug"]}
                      noStyle
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                    >
                      <Select
                        {...targetUnitProps}
                        defaultValue={
                          missionData?.missionTarget?.targetUnit?.name
                        }
                        loading={targetUnitLoading}
                        style={{ width: "50%" }}
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              );
            } else if (label === "First to Finish") {
              return (
                <>
                  <Form.Item
                    label="Rounds"
                    name="rounds"
                    rules={[{ required: true, message: "Please enter rounds" }]}
                  >
                    <InputNumber min={1} />
                  </Form.Item>

                  <Form.Item label="Time limit">
                    <Input.Group compact>
                      <Form.Item
                        name={["timeLimitMinutes", "amount"]}
                        noStyle
                        rules={[
                          { required: true, message: "Time limit is required" },
                        ]}
                      >
                        <InputNumber
                          style={{ width: "50%" }}
                          min={1}
                          placeholder="Amount"
                        />
                      </Form.Item>
                      <Form.Item
                        name={["timeLimitMinutes", "unit"]}
                        noStyle
                        rules={[
                          {
                            required: true,
                            message: "Time limit unit is required",
                          },
                        ]}
                      >
                        <Select
                          style={{ width: "50%" }}
                          placeholder="Unit"
                          options={[
                            { label: "Minutes", value: "minutes" },
                            { label: "Hours", value: "hours" },
                            { label: "Days", value: "days" },
                            { label: "Weeks", value: "weeks" },
                          ]}
                        />
                      </Form.Item>
                    </Input.Group>
                  </Form.Item>

                  <Form.List
                    name={["workoutsSets", 0, "workouts"]}
                    rules={[
                      {
                        validator: async (_, workouts) => {
                          if (!workouts || workouts.length < 1) {
                            return Promise.reject(
                              new Error("At least one workout is required")
                            );
                          }
                        },
                      },
                    ]}
                  >
                    {(
                      workoutFields,
                      { add: addWorkout, remove: removeWorkout }
                    ) => (
                      <>
                        {workoutFields.map((field) => (
                          <Space
                            key={field.key}
                            style={{ display: "flex", marginBottom: 8 }}
                            align="baseline"
                          >
                            <Form.Item
                              name={[field.name, "workoutID"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please select a workout",
                                },
                              ]}
                            >
                              <Select
                                {...workoutSelectProps}
                                loading={workoutLoading}
                                style={{ width: "200px" }}
                                placeholder="Select workout"
                              />
                            </Form.Item>
                            <Form.Item
                              name={[field.name, "reps"]}
                              rules={[
                                { required: true, message: "Missing reps" },
                              ]}
                            >
                              <InputNumber
                                placeholder="Reps"
                                min={1}
                                style={{ width: "100px" }}
                              />
                            </Form.Item>
                            <MinusCircleOutlined
                              onClick={() => removeWorkout(field.name)}
                            />
                          </Space>
                        ))}
                        <Form.Item
                          name="workoutsSets"
                          rules={[
                            {
                              required: true,
                              message: "At least one workout is required",
                            },
                          ]}
                          dependencies={["workoutsSets"]}
                        >
                          <Button
                            type="dashed"
                            onClick={() => addWorkout()}
                            block
                            icon={<PlusOutlined />}
                          >
                            Add Workout
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </>
              );
            } else if (label === "Calories") {
              return (
                <>
                  <Form.Item
                    label="Rounds"
                    name="rounds"
                    rules={[{ required: true, message: "Please enter rounds" }]}
                  >
                    <InputNumber min={1} />
                  </Form.Item>
                  <Form.List
                    name={["workoutsSets", 0, "workouts"]}
                    rules={[
                      {
                        validator: async (_, workouts) => {
                          if (!workouts || workouts.length < 1) {
                            return Promise.reject(
                              new Error("At least one workout is required")
                            );
                          }
                        },
                      },
                    ]}
                  >
                    {(
                      workoutFields,
                      { add: addWorkout, remove: removeWorkout }
                    ) => (
                      <>
                        {workoutFields.map((field) => (
                          <Space
                            key={field.key}
                            style={{ display: "flex", marginBottom: 8 }}
                            align="baseline"
                          >
                            <Form.Item
                              name={[field.name, "workoutID"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please select a workout",
                                },
                              ]}
                            >
                              <Select
                                {...workoutSelectProps}
                                loading={workoutLoading}
                                style={{ width: "200px" }}
                                placeholder="Select workout"
                              />
                            </Form.Item>
                            <Form.Item
                              name={[field.name, "reps"]}
                              rules={[
                                { required: true, message: "Missing reps" },
                              ]}
                            >
                              <InputNumber
                                placeholder="Reps"
                                min={1}
                                style={{ width: "100px" }}
                              />
                            </Form.Item>
                            <MinusCircleOutlined
                              onClick={() => removeWorkout(field.name)}
                            />
                          </Space>
                        ))}
                        <Form.Item
                          name="workoutsSets"
                          rules={[
                            {
                              required: true,
                              message: "At least one workout is required",
                            },
                          ]}
                          dependencies={["workoutsSets"]}
                        >
                          <Button
                            type="dashed"
                            onClick={() => addWorkout()}
                            block
                            icon={<PlusOutlined />}
                          >
                            Add Workout
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                  <Form.Item label="Target calories" required>
                    <Input.Group compact>
                      <Form.Item
                        name={["targetValue"]}
                        noStyle
                        rules={[
                          {
                            required: true,
                            type: "number",
                            min: 0,
                            message: "Please enter a positive number",
                          },
                        ]}
                      >
                        <InputNumber
                          min={0}
                          step="1"
                          style={{ width: "50%" }}
                          placeholder="Enter target value"
                        />
                      </Form.Item>
                      <Form.Item name={["targetUnitSlug"]} noStyle>
                        <Input
                          readOnly
                          value="CALORIES"
                          aria-label="Calories"
                          placeholder="Calories"
                          style={{ width: "10%" }}
                        />
                      </Form.Item>
                    </Input.Group>
                  </Form.Item>
                </>
              );
            } else if (label === "Rounds" || label === "Workouts") {
              return (
                <>
                  <Form.Item
                    label="Rounds"
                    name="rounds"
                    rules={[{ required: true, message: "Please enter rounds" }]}
                  >
                    <InputNumber min={1} />
                  </Form.Item>

                  <Form.List
                    name={["workoutsSets", 0, "workouts"]}
                    rules={[
                      {
                        validator: async (_, workouts) => {
                          if (!workouts || workouts.length < 1) {
                            return Promise.reject(
                              new Error("At least one workout is required")
                            );
                          }
                        },
                      },
                    ]}
                  >
                    {(
                      workoutFields,
                      { add: addWorkout, remove: removeWorkout }
                    ) => (
                      <>
                        {workoutFields.map((field) => (
                          <Space
                            key={field.key}
                            style={{ display: "flex", marginBottom: 8 }}
                            align="baseline"
                          >
                            <Form.Item
                              name={[field.name, "workoutID"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please select a workout",
                                },
                              ]}
                            >
                              <Select
                                {...workoutSelectProps}
                                loading={workoutLoading}
                                style={{ width: "200px" }}
                                placeholder="Select workout"
                              />
                            </Form.Item>
                            <Form.Item
                              name={[field.name, "reps"]}
                              rules={[
                                { required: true, message: "Missing reps" },
                              ]}
                            >
                              <InputNumber
                                placeholder="Reps"
                                min={1}
                                style={{ width: "100px" }}
                              />
                            </Form.Item>
                            <MinusCircleOutlined
                              onClick={() => removeWorkout(field.name)}
                            />
                          </Space>
                        ))}
                        <Form.Item
                          name="workoutsSets"
                          rules={[
                            {
                              required: true,
                              message: "At least one workout is required",
                            },
                          ]}
                          dependencies={["workoutsSets"]}
                        >
                          <Button
                            type="dashed"
                            onClick={() => addWorkout()}
                            block
                            icon={<PlusOutlined />}
                          >
                            Add Workout
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </>
              );
            }
            return null;
          }}
        </Form.Item>

        <Form.Item
          label="Ends At"
          name={["endsAt"]}
          initialValue={missionData?.endsAt ? dayjs(missionData.endsAt) : undefined}
          rules={[{ required: true, message: "Please select an end date/time" }]}
        >
          <DatePicker
            showTime
            style={{ width: "100%" }}
            placement="bottomLeft"
          />
        </Form.Item>
      </Form>
    </Edit>
  );
};
